import React, { useMemo, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert } from 'reactstrap';
import Breadcrumbs from 'components/Common/Breadcrumb';
import TableContainer from 'components/Common/TableContainer';
import { Link } from 'react-router-dom';
import { useListQuery } from 'helpers/http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'helpers/http-client/httpConstants';
import { ColumnDef } from '@tanstack/react-table';
import { GenericFormModal } from 'components/Modals/AddGenericModal';
import { FormType } from 'components/Modals/formTypes';

interface Medicine {
  id: string;
  name: string;
  alias?: string;
  dosageForm: string;
  strength?: string;
  prescriptionRequired: boolean;
  medicineType?: string;
  totalInventoryPacks: number;
  availableStockQuantity: number;
  earliestExpiryDate: string | null;
  latestPrice: number | null;
  currency: string | null;
  effectiveDate: string | null;
}

const MedicineList: React.FC = () => {
  const [modalFormType, setModalFormType] = useState<FormType | null>(null);

  const { data, isLoading, isError, refetch } = useListQuery(
    `${API_ENDPOINTS.MEDICINES}/inventory-summary`,
  );

  const medicineData: Medicine[] = useMemo(() => {
    if (!data) return [];
    return data.map((medicine: any) => ({
      id: medicine.id,
      name: medicine.name,
      alias: medicine.alias || '-',
      dosageForm: medicine.dosageForm,
      strength: medicine.strength || '-',
      prescriptionRequired: medicine.prescriptionRequired ?? false,
      medicineType: medicine.medicineType || '-',
      totalInventoryPacks: medicine.totalInventoryPacks ?? 0,
      availableStockQuantity: medicine.availableStockQuantity ?? 0,
      earliestExpiryDate: medicine.earliestExpiryDate
        ? new Date(medicine.earliestExpiryDate).toLocaleDateString()
        : '-',
      latestPrice: medicine.latestPrice ?? null,
      currency: medicine.currency ?? null,
      effectiveDate: medicine.effectiveDate
        ? new Date(medicine.effectiveDate).toLocaleDateString()
        : '-',
    }));
  }, [data]);

  const columns: ColumnDef<Medicine>[] = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        enableSorting: true,
        cell: (cellProps) => (
          <Link to={`/medicines/${cellProps.row.original.id}`} className="text-dark fw-bold">
            {cellProps.row.original.name}
          </Link>
        ),
      },
      // {
      //   header: 'Alias',
      //   accessorKey: 'alias',
      // },
      {
        header: 'Dosage Form',
        accessorKey: 'dosageForm',
      },
      {
        header: 'Strength',
        accessorKey: 'strength',
      },
      // {
      //   header: 'Type',
      //   accessorKey: 'medicineType',
      // },
      {
        header: 'Prescription Required',
        accessorKey: 'prescriptionRequired',
        cell: ({ row }) => (row.original.prescriptionRequired ? 'Yes' : 'No'),
      },
      {
        header: 'Total Pieces',
        accessorKey: 'availableStockQuantity',
      },
      {
        header: 'Earliest Expiry',
        accessorKey: 'earliestExpiryDate',
      },
      {
        header: 'Latest Price',
        accessorKey: 'latestPrice',
        cell: ({ row }) =>
          row.original.latestPrice !== null && row.original.currency
            ? `${row.original.latestPrice} ${row.original.currency}`
            : '-',
      },
      {
        header: 'Price Effective Date',
        accessorKey: 'effectiveDate',
      },
    ],
    [],
  );

  const handleModalSuccess = () => {
    setModalFormType(null);
    refetch();
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Inventory" breadcrumbItem="Medicines" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : isError ? (
                  <Alert color="danger">Failed to load medicines. Please try again later.</Alert>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={medicineData}
                    isGlobalFilter={true}
                    isAddButton={true}
                    handleUserClick={() => setModalFormType(FormType.MEDICINE)}
                    isPagination={true}
                    isCustomPageSize={true}
                    buttonClass="btn btn-success btn-rounded"
                    buttonName="Add Medicine"
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-borderless"
                    theadClass="table-light"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    paginationClass="pagination"
                    SearchPlaceholder="Search medicines..."
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <GenericFormModal
          isOpen={modalFormType === FormType.MEDICINE}
          toggle={() => setModalFormType(null)}
          formType={FormType.MEDICINE}
          onSuccess={handleModalSuccess}
        />
      </Container>
    </div>
  );
};

export default MedicineList;
